import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { Wishlist } from '../types';
import { logout } from './authSlice';
import { extractApiErrorMessage } from '../utils/errorHandler';

interface WishlistState {
  wishlist: Wishlist | null;
  loading: boolean;
  mutating: boolean;
  error: string | null;
}

const initialState: WishlistState = {
  wishlist: null,
  loading: false,
  mutating: false,
  error: null
};

export const fetchMyWishlist = createAsyncThunk<Wishlist, void, { rejectValue: string }>(
  'wishlist/fetchMyWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.WISHLIST}`);
      return response.data as Wishlist;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to load wishlist'));
    }
  }
);

export const addToWishlist = createAsyncThunk<Wishlist, { productId: string }, { rejectValue: string }>(
  'wishlist/addToWishlist',
  async ({ productId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.WISHLIST}/items`, { productId });
      return response.data as Wishlist;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to add to wishlist'));
    }
  }
);

export const removeFromWishlist = createAsyncThunk<Wishlist, { productId: string }, { rejectValue: string }>(
  'wishlist/removeFromWishlist',
  async ({ productId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`${API_ENDPOINTS.WISHLIST}/items`, {
        data: { productId }
      });
      return response.data as Wishlist;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to remove from wishlist'));
    }
  }
);

export const clearMyWishlist = createAsyncThunk<Wishlist, void, { rejectValue: string }>(
  'wishlist/clearMyWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.WISHLIST}/clear`);
      return response.data as Wishlist;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to clear wishlist'));
    }
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlist = action.payload;
      })
      .addCase(fetchMyWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to load wishlist';
      })
      .addCase(addToWishlist.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.mutating = false;
        state.wishlist = action.payload;
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to add to wishlist';
      })
      .addCase(removeFromWishlist.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.mutating = false;
        state.wishlist = action.payload;
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to remove from wishlist';
      })
      .addCase(clearMyWishlist.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(clearMyWishlist.fulfilled, (state, action) => {
        state.mutating = false;
        state.wishlist = action.payload;
      })
      .addCase(clearMyWishlist.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to clear wishlist';
      })
      .addCase(logout, (state) => {
        state.wishlist = null;
        state.loading = false;
        state.mutating = false;
        state.error = null;
      });
  }
});

export default wishlistSlice.reducer;
